import { api } from "@breakout/backend/convex/_generated/api"
import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { useAction } from "convex/react"
import { useState } from "react"
import { optimizeAvatarImage, readFileAsBase64, validateImageFile } from "../../lib/image"

export type UploadStatus = "idle" | "validating" | "optimizing" | "uploading" | "done" | "error"

interface UploadResult {
	url: string
}

export function useUploadAvatar() {
	const uploadAvatarAction = useAction(api.domains.user.uploads.uploadAvatar)
	const [status, setStatus] = useState<UploadStatus>("idle")

	const mutation = useMutation({
		mutationFn: async (file: File): Promise<UploadResult | null> => {
			// Validate file
			setStatus("validating")
			const validation = validateImageFile(file)
			if (!validation.valid) {
				throw new Error(validation.error)
			}

			// Optimize image (resize, compress, square crop)
			setStatus("optimizing")
			const optimizedFile = await optimizeAvatarImage(file)

			// Read as base64
			const base64 = await readFileAsBase64(optimizedFile)

			// Upload to R2 via Convex
			setStatus("uploading")
			const result = await uploadAvatarAction({
				imageData: base64,
				contentType: optimizedFile.type,
			})

			if ("error" in result) {
				throw new Error(result.error)
			}

			setStatus("done")
			return { url: result.url }
		},
		onError: error => {
			setStatus("error")

			// Log upload failures to Sentry
			Sentry.captureException(error, {
				tags: { operation: "uploadAvatar", domain: "uploads" },
				extra: {
					status,
				},
			})
		},
		onSettled: () => {
			setTimeout(() => setStatus("idle"), 2000)
		},
	})

	return {
		uploadAvatar: mutation.mutateAsync,
		status,
		error: mutation.error,
		isUploading: ["validating", "optimizing", "uploading"].includes(status),
		reset: () => {
			setStatus("idle")
			mutation.reset()
		},
	}
}
